import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "../css/AddUnit.css";

const AddSale = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || "";

  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [quantity, setQuantity] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [purchaseDate, setPurchaseDate] = useState("");

  // Product search dropdown state
  const [productSearch, setProductSearch] = useState("");
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProductIndex, setSelectedProductIndex] = useState(-1);
  const productDropdownRef = useRef(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API_URL}product-list.php`);
        const data = await response.json();
        if (data.status && Array.isArray(data.data)) {
          setProducts(data.data);
          setFilteredProducts(data.data);
        }
      } catch (error) {
        showToast("Failed to load products", "error");
      }
    };
    fetchProducts();
  }, [API_URL]);

  // Filter products based on search
  useEffect(() => {
    if (!productSearch.trim()) {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter((product) =>
        product.product.toLowerCase().includes(productSearch.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
    setSelectedProductIndex(-1);
  }, [productSearch, products]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        productDropdownRef.current &&
        !productDropdownRef.current.contains(event.target)
      ) {
        setShowProductDropdown(false);
        setSelectedProductIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  // Handle product selection
  const handleProductSelect = (product) => {
    setProductId(product.id.toString());
    setProductSearch(product.product);
    setShowProductDropdown(false);
    setSelectedProductIndex(-1);
  };

  // Handle product keyboard navigation
  const handleProductKeyDown = (e) => {
    if (!showProductDropdown || filteredProducts.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedProductIndex((prev) => {
          const newIndex = prev < filteredProducts.length - 1 ? prev + 1 : 0;
          // Scroll to the selected item
          setTimeout(() => {
            const dropdown =
              productDropdownRef.current?.querySelector(".product-dropdown");
            const selectedItem = dropdown?.querySelector(
              `li:nth-child(${newIndex + 1})`
            );
            if (selectedItem) {
              selectedItem.scrollIntoView({
                block: "nearest",
                behavior: "smooth",
              });
            }
          }, 0);
          return newIndex;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedProductIndex((prev) => {
          const newIndex = prev > 0 ? prev - 1 : filteredProducts.length - 1;
          // Scroll to the selected item
          setTimeout(() => {
            const dropdown =
              productDropdownRef.current?.querySelector(".product-dropdown");
            const selectedItem = dropdown?.querySelector(
              `li:nth-child(${newIndex + 1})`
            );
            if (selectedItem) {
              selectedItem.scrollIntoView({
                block: "nearest",
                behavior: "smooth",
              });
            }
          }, 0);
          return newIndex;
        });
        break;
      case "Enter":
        e.preventDefault();
        if (
          selectedProductIndex >= 0 &&
          selectedProductIndex < filteredProducts.length
        ) {
          handleProductSelect(filteredProducts[selectedProductIndex]);
        }
        break;
      case "Escape":
        setShowProductDropdown(false);
        setSelectedProductIndex(-1);
        break;
    }
  };

  // Handle product search input
  const handleProductSearchChange = (e) => {
    setProductSearch(e.target.value);
    setShowProductDropdown(true);
    setSelectedProductIndex(-1);
    if (!e.target.value.trim()) {
      setProductId("");
    }
  };

  // Handle product search focus
  const handleProductSearchFocus = () => {
    setShowProductDropdown(true);
    setSelectedProductIndex(-1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const saleData = {
      product_id: productId,
      quantity: Number(quantity),
      purchase_date: purchaseDate,
    };

    try {
      const response = await fetch(`${API_URL}sales.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(saleData),
      });

      const data = await response.json();

      if (data.status === true) {
        showToast("Sale recorded successfully!", "success");
        setTimeout(() => navigate("/sale-history"), 1500);
      } else {
        showToast(data.message || "Failed to record sale.", "error");
      }
    } catch (error) {
      showToast("Network error. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-category-content">
      <div className="page-header" style={{ display: "block" }}>
        <h1>Record a Sale</h1>
        <p>Select a product and enter the quantity sold</p>
      </div>
      <div className="form-container">
        <form onSubmit={handleSubmit} className="add-unit-form">
          <div className="form-group">
            <label htmlFor="productId">Product</label>
            <div
              ref={productDropdownRef}
              className={`product-search-container ${
                showProductDropdown ? "open" : ""
              }`}
            >
              <div className="search-input-wrapper">
                <input
                  type="text"
                  id="productId"
                  value={productSearch}
                  onChange={handleProductSearchChange}
                  onFocus={handleProductSearchFocus}
                  onKeyDown={handleProductKeyDown}
                  placeholder="Search products..."
                  disabled={isSubmitting}
                  className="search-input"
                />
                <button
                  type="button"
                  className="dropdown-toggle"
                  onClick={() => {
                    setShowProductDropdown(!showProductDropdown);
                    if (!showProductDropdown) {
                      setTimeout(() => {
                        const input =
                          productDropdownRef.current?.querySelector("input");
                        input?.focus();
                        // Scroll to selected item if exists
                        if (selectedProductIndex >= 0) {
                          const selectedElement =
                            productDropdownRef.current?.querySelector(
                              ".product-dropdown li.selected"
                            );
                          if (selectedElement) {
                            selectedElement.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                          }
                        }
                      }, 100);
                    }
                  }}
                  disabled={isSubmitting}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
              </div>
              {showProductDropdown && (
                <ul className="product-dropdown">
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map((product, index) => (
                      <li
                        key={product.id}
                        data-product-id={product.id}
                        onClick={() => handleProductSelect(product)}
                        className={
                          selectedProductIndex === index ? "selected" : ""
                        }
                        onMouseEnter={(e) => {
                          // Remove selection from other items
                          productDropdownRef.current
                            ?.querySelectorAll(".product-dropdown li")
                            .forEach((item) => {
                              item.classList.remove("selected");
                            });
                          // Add selection to current item
                          e.target.classList.add("selected");
                          setSelectedProductIndex(index);
                        }}
                      >
                        {product.product}
                      </li>
                    ))
                  ) : (
                    <li className="no-results">No products found</li>
                  )}
                </ul>
              )}
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="quantity">Quantity Sold</label>
            <input
              type="number"
              id="quantity"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              required
              disabled={isSubmitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="purchaseDate">Sale Date</label>
            <input
              type="date"
              id="purchaseDate"
              value={purchaseDate}
              onChange={(e) => setPurchaseDate(e.target.value)}
              required
              disabled={isSubmitting}
            />
          </div>
          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : "Record Sale"}
          </button>
        </form>
      </div>
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button
              className="toast-close"
              onClick={() => setToast({ show: false, message: "", type: "" })}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddSale;
